import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Stack, Surface } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Badge,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Tabs,
  Alert,
  useToast,
} from "@/components/ui";
import {
  BusinessNotesPanel,
  BusinessPaymentsCard,
  BusinessPeopleCard,
  BusinessSafetyPanel,
  BusinessMembershipsCard,
  BusinessSportsCard,
  BusinessStats,
  BusinessTimelinePanel,
} from "../components/BusinessOverviewPanels";
import type { BusinessNoteDto, BusinessOverviewResponse } from "../types";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createBusinessesApi } from "../api";
import {
  businessStatusTone,
  capabilityStatusTone,
  formatLabel,
  verificationStatusTone,
  visibilityStatusTone,
  documentsFromRequirements,
  answerLabel,
  type RequirementDocument,
} from "../labels";
import type {
  BusinessDetailResponse,
  CapabilityAction,
  CapabilityDto,
  RelationshipAction,
  RelationshipDto,
  TypeCapabilityDto,
  VerificationDto,
} from "../types";
import { BusinessActionDialog } from "../components/BusinessActionDialog";
import { canApplyAction } from "../lifecycle";
import { ProfileEditDrawer } from "../components/CreateAndProfileDialogs";
import {
  CreateCapabilityDialog,
  CreateRelationshipDialog,
  ReasonActionDialog,
  TransferOwnershipDialog,
} from "../components/RelationshipCapabilityDialogs";
import {
  AttachFileDialog,
  DecideVerificationDialog,
  RequestVerificationDialog,
} from "../components/VerificationFileDialogs";
import { useLiveRefresh } from "@/lib/useLiveRefresh";
import { VerificationDocumentsPanel } from "../components/VerificationDocumentsPanel";
import type { BusinessAction } from "../types";


/**
 * When the application arrived. i29 gave `business` a created_at because a
 * review queue has to be orderable newest-first, and a random uuid is not a
 * date.
 */
function formatApplied(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/**
 * Turn a requirement key into something a reviewer can read.
 *
 * Most keys title-case cleanly; the ones that do not are acronyms and proper
 * nouns, where a generic transform produces "Tin" and "Bir 2303".
 */

export function BusinessDetailPage() {
  const { businessId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createBusinessesApi(client), [client]);
  const toast = useToast();

  const [detail, setDetail] = useState<BusinessDetailResponse | null>(null);
  const [typeCapabilities, setTypeCapabilities] = useState<TypeCapabilityDto[]>(
    [],
  );
  const [typeName, setTypeName] = useState<string | null>(null);
  /**
   * Documents for this type, served from the backend so HQ and Play agree.
   * `null` = the type did not load, so what is owed is unknown — never `[]`,
   * which would read as "nothing owed" and silence the approve warning.
   */
  const [typeDocuments, setTypeDocuments] = useState<RequirementDocument[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [statusOpen, setStatusOpen] = useState(false);
  const [statusPreset, setStatusPreset] = useState<{
    action: BusinessAction;
    requiredDocuments?: string[];
  } | null>(null);
  // null = not known (documents loading, failed, or not readable by this reviewer).
  const [outstandingDocuments, setOutstandingDocuments] = useState<string[] | null>(null);
  /** Bumped by live refresh so the documents panel re-reads with the page. */
  const [documentsRefresh, setDocumentsRefresh] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);
  const [relOpen, setRelOpen] = useState(false);
  const [relAction, setRelAction] = useState<{
    id: string;
    action: RelationshipAction;
  } | null>(null);
  const [transferOpen, setTransferOpen] = useState(false);
  const [capOpen, setCapOpen] = useState(false);
  const [capAction, setCapAction] = useState<{
    id: string;
    action: CapabilityAction;
  } | null>(null);
  const [requestVerificationOpen, setRequestVerificationOpen] = useState(false);
  const [decideVerification, setDecideVerification] =
    useState<VerificationDto | null>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [overview, setOverview] = useState<BusinessOverviewResponse | null>(null);
  const [overviewError, setOverviewError] = useState<string | null>(null);
  const [noteBusy, setNoteBusy] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [tab, setTab] = useState("performance");

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Business Center", to: "/app/businesses" },
    {
      label:
        detail?.business.display_name ??
        detail?.business.public_id ??
        "Business Detail",
    },
  ]);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!businessId) return;
    if (!opts?.silent) setLoading(true);
    setError(null);
    try {
      const response = await api.getDetail(businessId);
      setDetail(response);
      try {
        const type = await api.getType(response.business.business_type_id);
        setTypeName(type.business_type.name);
        setTypeCapabilities(type.capabilities);
        setTypeDocuments(documentsFromRequirements(type.business_type));
      } catch {
        setTypeName(null);
        setTypeCapabilities([]);
        setTypeDocuments(null);
      }
    } catch (err) {
      setError(apiErrorMessage(err));
      // A failed background refresh must not blank a record being read.
      if (!opts?.silent) setDetail(null);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, [api, businessId]);

  /**
   * Business 360 (people, performance, money, notes, history). Read separately
   * so a failure here reports itself instead of hiding the application.
   */
  const loadOverview = useCallback(async () => {
    if (!businessId) return;
    try {
      setOverview(await api.getOverview(businessId));
      setOverviewError(null);
    } catch (err) {
      setOverviewError(apiErrorMessage(err));
    }
  }, [api, businessId]);

  useEffect(() => {
    void load();
    void loadOverview();
  }, [load, loadOverview]);

  /**
   * An applicant can resubmit or cancel while this page is open. Acting on a
   * stale record is how a reviewer approves something that was withdrawn.
   */
  useLiveRefresh(() => {
    void load({ silent: true });
    void loadOverview();
    setDocumentsRefresh((n) => n + 1);
  });

  async function addNote(body: string): Promise<boolean> {
    setNoteBusy(true);
    setNoteError(null);
    try {
      await api.addNote(businessId, { body });
      await loadOverview();
      return true;
    } catch (err) {
      setNoteError(apiErrorMessage(err));
      return false;
    } finally {
      setNoteBusy(false);
    }
  }

  async function pinNote(note: BusinessNoteDto) {
    try {
      await api.pinNote(businessId, note.id, !note.pinned);
      await loadOverview();
    } catch (err) {
      toast.push({ title: "Could not update note", description: apiErrorMessage(err), tone: "danger" });
    }
  }

  if (loading) {
    return <LoadingState message="Loading Business Detail…" />;
  }

  if (error || !detail) {
    return (
      <EmptyState
        title="Business unavailable"
        description={error ?? "Business not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const { business, relationships, capabilities, verifications } = detail;

  return (
    <Stack gap={24}>
      <SectionHeader
        title={business.display_name}
        description={`Public ID ${business.public_id}`}
        actions={
          <Flex gap={8} wrap>
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(`/app/bookings?business_id=${business.id}`)
              }
            >
              Bookings
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(
                  `/app/businesses/resources?business_id=${business.id}`,
                )
              }
            >
              Bookable Resources
            </Button>
            <Button variant="secondary" onClick={() => setProfileOpen(true)}>
              Edit profile
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setStatusPreset(null);
                setStatusOpen(true);
              }}
            >
              Status actions
            </Button>
            <Button variant="secondary" onClick={() => setTransferOpen(true)}>
              Transfer ownership
            </Button>
            <Button variant="secondary" onClick={() => setAttachOpen(true)}>
              Attach file
            </Button>
          </Flex>
        }
      />

      <ActionBar
        left={
          <Flex gap={8} align="center" wrap>
            <StatusChip tone={businessStatusTone(business.status)}>
              Status: {formatLabel(business.status)}
            </StatusChip>
            <StatusChip
              tone={verificationStatusTone(business.verification_status)}
            >
              Verification: {formatLabel(business.verification_status)}
            </StatusChip>
            <StatusChip
              tone={visibilityStatusTone(business.visibility_status)}
            >
              Visibility: {formatLabel(business.visibility_status)}
            </StatusChip>
            {business.closed_at ? (
              <Badge tone="danger">Closed {business.closed_at}</Badge>
            ) : null}
          </Flex>
        }
      />

      <Card
        header={
          <Text role="title" as="h3">
            Identity
          </Text>
        }
      >
        <Stack gap={8}>
          <Text role="body" as="p">
            Legal name: {business.legal_name}
          </Text>
          <Flex gap={8} align="center" wrap>
            <Text role="body" as="span">
              Business Type:
            </Text>
            {typeName ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  void navigate(
                    `/app/businesses/types/${business.business_type_id}`,
                  )
                }
              >
                {typeName}
              </Button>
            ) : (
              <Text role="body" as="span">
                {business.business_type_id}
              </Text>
            )}
          </Flex>
          <Text role="body" as="p">
            Market: {overview?.market ? `${overview.market.name} (${overview.market.code})` : business.market_id}
          </Text>
          <Text role="body" as="p">
            Timezone: {business.timezone_id}
          </Text>
          <Text role="body" as="p">
            Description: {business.description ?? "—"}
          </Text>
          <Text role="body" as="p">
            Applied: {formatApplied(business.created_at)}
          </Text>
          <Text role="caption" color="muted" as="p">
            Business ID: {business.id}
          </Text>
        </Stack>
      </Card>

      {business.application_answers &&
      Object.keys(business.application_answers).length > 0 ? (
        <Card
          header={
            <Text role="h2" as="h2">
              Application details
            </Text>
          }
        >
          {/* What the applicant typed on the registration form. These used to
              be crammed into `description` as one paragraph, because `business`
              had no column for a TIN or an address. i30 gave them one. */}
          <Stack gap={8}>
            {Object.entries(business.application_answers).map(([key, value]) => (
              <Flex key={key} gap={8} align="start" wrap>
                <Text role="body" color="muted" as="span">
                  {answerLabel(key)}:
                </Text>
                <Text role="body" as="span">
                  {value}
                </Text>
              </Flex>
            ))}
          </Stack>
        </Card>
      ) : null}

      <VerificationDocumentsPanel
        api={api}
        businessId={business.id}
        requirements={typeDocuments}
        refreshToken={documentsRefresh}
        businessStatus={business.status}
        answers={business.application_answers}
        canSendBack={canApplyAction("request_more_information", business.status)}
        onOutstandingChange={setOutstandingDocuments}
        onSendBack={(keys) => {
          setStatusPreset({ action: "request_more_information", requiredDocuments: keys });
          setStatusOpen(true);
        }}
      />

      {overviewError ? (
        <Alert tone="danger" title="Performance, people and history could not be loaded">
          {overviewError}
        </Alert>
      ) : null}

      <Tabs
        value={tab}
        onValueChange={setTab}
        items={[
          {
            id: "performance",
            label: "Performance",
            content: overview ? (
              <Stack gap={16}>
                <BusinessStats overview={overview} />
                <BusinessSportsCard overview={overview} />
                <BusinessPaymentsCard overview={overview} />
                <BusinessMembershipsCard businessId={businessId} />
              </Stack>
            ) : (
              <LoadingState message="Loading…" />
            ),
          },
          {
            id: "people",
            label: `People${overview ? ` (${overview.people.length})` : ""}`,
            content: overview ? (
              <BusinessPeopleCard overview={overview} />
            ) : (
              <LoadingState message="Loading…" />
            ),
          },
          {
            id: "setup",
            label: "Relationships, capabilities & verification",
            content: (
              <Stack gap={24}>
                <Surface>
                  <Stack gap={16}>
                    <Flex justify="space-between" align="center" wrap gap={8}>
                      <Text role="h2" as="h2">
                        Business Relationships
                      </Text>
                      <Button size="sm" variant="secondary" onClick={() => setRelOpen(true)}>
                        Add relationship
                      </Button>
                    </Flex>
                    {relationships.length === 0 ? (
                      <EmptyState
                        title="No relationships"
                        description="Owner, Manager, Staff, and other relationship types stay separate from Business status."
                        reason="none"
                      />
                    ) : (
                      <RelationshipsTable
                        rows={relationships}
                        onAction={(id, action) => setRelAction({ id, action })}
                      />
                    )}
                  </Stack>
                </Surface>

                <Surface>
                  <Stack gap={16}>
                    <Flex justify="space-between" align="center" wrap gap={8}>
                      <Text role="h2" as="h2">
                        Business Capabilities
                      </Text>
                      <Button size="sm" variant="secondary" onClick={() => setCapOpen(true)}>
                        Add capability
                      </Button>
                    </Flex>
                    {capabilities.length === 0 ? (
                      <EmptyState
                        title="No capabilities"
                        description="Empty capability state may mean type ineligibility or incomplete setup. Bookable Resources attach to a capability, not to the Business Type itself."
                        reason="none"
                      />
                    ) : (
                      <CapabilitiesTable
                        rows={capabilities}
                        onAction={(id, action) => setCapAction({ id, action })}
                        onOpenResources={(capabilityId) =>
                          void navigate(
                            `/app/businesses/resources?business_id=${business.id}&business_capability_id=${capabilityId}`,
                          )
                        }
                      />
                    )}
                  </Stack>
                </Surface>

                <Surface>
                  <Stack gap={16}>
                    <Flex justify="space-between" align="center" wrap gap={8}>
                      <Text role="h2" as="h2">
                        Verifications
                      </Text>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setRequestVerificationOpen(true)}
                      >
                        Request verification
                      </Button>
                    </Flex>
                    {verifications.length === 0 ? (
                      <EmptyState
                        title="No verification records"
                        description="Request business identity or ownership verification when authorized."
                        reason="none"
                      />
                    ) : (
                      <Table>
                        <Thead>
                          <Tr>
                            <Th>Type</Th>
                            <Th>Status</Th>
                            <Th>Requested</Th>
                            <Th>Reviewed</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {verifications.map((row) => (
                            <Tr key={row.id}>
                              <Td>{formatLabel(row.verification_type)}</Td>
                              <Td>
                                <StatusChip tone={verificationStatusTone(row.status)}>
                                  {formatLabel(row.status)}
                                </StatusChip>
                              </Td>
                              <Td>{row.requested_at ?? "—"}</Td>
                              <Td>{row.reviewed_at ?? "—"}</Td>
                              <Td>
                                <Button
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => setDecideVerification(row)}
                                >
                                  Decide
                                </Button>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    )}
                  </Stack>
                </Surface>
              </Stack>
            ),
          },
          {
            id: "safety",
            label: `Safety${overview && overview.reports_against.length ? ` (${overview.reports_against.length})` : ""}`,
            content: overview ? (
              <BusinessSafetyPanel overview={overview} />
            ) : (
              <LoadingState message="Loading…" />
            ),
          },
          {
            id: "notes",
            label: `Notes${overview && overview.notes.length ? ` (${overview.notes.length})` : ""}`,
            content: overview ? (
              <BusinessNotesPanel
                notes={overview.notes}
                busy={noteBusy}
                error={noteError}
                onAdd={addNote}
                onPin={(note) => void pinNote(note)}
              />
            ) : (
              <LoadingState message="Loading…" />
            ),
          },
          {
            id: "history",
            label: "History",
            content: overview ? (
              <BusinessTimelinePanel overview={overview} />
            ) : (
              <LoadingState message="Loading…" />
            ),
          },
        ]}
      />

      <BusinessActionDialog
        open={statusOpen}
        status={business.status}
        documents={typeDocuments ?? []}
        preset={statusPreset}
        outstandingDocuments={outstandingDocuments}
        onClose={() => {
          setStatusOpen(false);
          setStatusPreset(null);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            /**
             * Approving from `under_review` is two transitions, not one.
             * `activate` is only legal from `verified`, so a reviewer choosing
             * it here means "verify then activate" — doing only the first would
             * leave the business a step short of bookable with no obvious sign
             * why.
             *
             * Sequential rather than atomic: if the second call fails the
             * business sits at `verified`, which is a legal state the next
             * dialog offers `activate` from. Nothing is stranded.
             */
            if (input.action === "activate" && business.status === "under_review") {
              await api.applyAction(business.id, "mark_verified", {
                ...(input.reason ? { reason: input.reason } : {}),
              });
            }
            await api.applyAction(business.id, input.action, {
              ...(input.reason ? { reason: input.reason } : {}),
              ...(input.approvalId ? { approval_id: input.approvalId } : {}),
              ...(input.requiredDocuments
                ? { required_documents: input.requiredDocuments }
                : {}),
            });
            toast.push({
              title: "Business action applied",
              description: formatLabel(input.action),
              tone: "success",
            });
            setStatusOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <ProfileEditDrawer
        open={profileOpen}
        displayName={business.display_name}
        legalName={business.legal_name}
        description={business.description ?? ""}
        timezoneId={business.timezone_id}
        visibilityStatus={business.visibility_status}
        onClose={() => {
          setProfileOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (patch) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.updateProfile(business.id, patch);
            toast.push({ title: "Profile updated", tone: "success" });
            setProfileOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <CreateRelationshipDialog
        open={relOpen}
        onClose={() => {
          setRelOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.createRelationship(business.id, input);
            toast.push({ title: "Relationship created", tone: "success" });
            setRelOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <ReasonActionDialog
        open={relAction !== null}
        title={`${formatLabel(relAction?.action ?? "action")} relationship`}
        onClose={() => {
          setRelAction(null);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (reason) => {
          if (!relAction) return;
          setBusy(true);
          setActionError(null);
          try {
            await api.applyRelationshipAction(
              business.id,
              relAction.id,
              relAction.action,
              { reason },
            );
            toast.push({
              title: "Relationship updated",
              description: formatLabel(relAction.action),
              tone: "success",
            });
            setRelAction(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <TransferOwnershipDialog
        open={transferOpen}
        onClose={() => {
          setTransferOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.transferOwnership(business.id, input);
            toast.push({
              title: "Ownership transfer submitted",
              tone: "success",
            });
            setTransferOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <CreateCapabilityDialog
        open={capOpen}
        typeCapabilities={typeCapabilities}
        onClose={() => {
          setCapOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.createCapability(business.id, input);
            toast.push({ title: "Capability created", tone: "success" });
            setCapOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <ReasonActionDialog
        open={capAction !== null}
        title={`${formatLabel(capAction?.action ?? "action")} capability`}
        onClose={() => {
          setCapAction(null);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (reason) => {
          if (!capAction) return;
          setBusy(true);
          setActionError(null);
          try {
            await api.applyCapabilityAction(
              business.id,
              capAction.id,
              capAction.action,
              { reason },
            );
            toast.push({
              title: "Capability updated",
              description: formatLabel(capAction.action),
              tone: "success",
            });
            setCapAction(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <RequestVerificationDialog
        open={requestVerificationOpen}
        onClose={() => {
          setRequestVerificationOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async ({ verificationType }) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.requestVerification(business.id, {
              verification_type: verificationType,
            });
            toast.push({ title: "Verification requested", tone: "success" });
            setRequestVerificationOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <DecideVerificationDialog
        open={decideVerification !== null}
        onClose={() => {
          setDecideVerification(null);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          if (!decideVerification) return;
          setBusy(true);
          setActionError(null);
          try {
            await api.decideVerification(business.id, decideVerification.id, {
              action: input.action,
              decision_reason: input.decisionReason,
              ...(input.approvalId ? { approval_id: input.approvalId } : {}),
              ...(input.isOverride !== undefined
                ? { is_override: input.isOverride }
                : {}),
            });
            toast.push({
              title: "Verification decision recorded",
              tone: "success",
            });
            setDecideVerification(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <AttachFileDialog
        open={attachOpen}
        onClose={() => {
          setAttachOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.attachFile(business.id, input);
            toast.push({ title: "File Asset attached", tone: "success" });
            setAttachOpen(false);
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

function RelationshipsTable({
  rows,
  onAction,
}: {
  rows: RelationshipDto[];
  onAction: (id: string, action: RelationshipAction) => void;
}) {
  const actions: RelationshipAction[] = [
    "activate",
    "restrict",
    "suspend",
    "revoke",
    "remove",
  ];
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Type</Th>
          <Th>User ID</Th>
          <Th>Status</Th>
          <Th>Effective from</Th>
          <Th>Actions</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <Tr key={row.id}>
            <Td>{formatLabel(row.relationship_type)}</Td>
            <Td>{row.user_id}</Td>
            <Td>{formatLabel(row.status)}</Td>
            <Td>{row.effective_from}</Td>
            <Td>
              <Flex gap={4} wrap>
                {actions.map((action) => (
                  <Button
                    key={action}
                    size="sm"
                    variant="ghost"
                    onClick={() => onAction(row.id, action)}
                  >
                    {formatLabel(action)}
                  </Button>
                ))}
              </Flex>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

function CapabilitiesTable({
  rows,
  onAction,
  onOpenResources,
}: {
  rows: CapabilityDto[];
  onAction: (id: string, action: CapabilityAction) => void;
  onOpenResources: (capabilityId: string) => void;
}) {
  const actions: CapabilityAction[] = [
    "enable",
    "activate",
    "pause",
    "restrict",
    "retire",
    "resume",
  ];
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Capability</Th>
          <Th>Status</Th>
          <Th>Effective from</Th>
          <Th>Actions</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <Tr key={row.id}>
            <Td>{formatLabel(row.capability_type)}</Td>
            <Td>
              <StatusChip tone={capabilityStatusTone(row.status)}>
                {formatLabel(row.status)}
              </StatusChip>
            </Td>
            <Td>{row.effective_from}</Td>
            <Td>
              <Flex gap={4} wrap>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onOpenResources(row.id)}
                >
                  Resources
                </Button>
                {actions.map((action) => (
                  <Button
                    key={action}
                    size="sm"
                    variant="ghost"
                    onClick={() => onAction(row.id, action)}
                  >
                    {formatLabel(action)}
                  </Button>
                ))}
              </Flex>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}
